/* global EpgEnabled ShowcaseRedesign */
import React, { Component } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { noop } from 'helpers/GlobalHelper';
import { Link, withRouter, browserHistory } from 'react-router';
import Dotdotdot from 'react-dotdotdot';
import { getTimestamp } from 'helpers/DateTimeHelper';
import Badge from 'components/Badge';
import ProgressBar from 'components/ProgressBar';
import MoreInfoButton from 'components/MoreInfoButton';
import {
  COLLECTION_TYPE_FTSLOCAL,
  COLLECTION_TYPE_LOCAL_CLIPS,
  isDescTooLong,
  isSportsContent,
  isInternalLink,
  setLocalChannelCookies,
  setOutOfMarketLocalCookies,
  truncateText,
  VIDEO_TYPE,
} from 'helpers/ContentHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import {
  setSegmentContextSourceLive,
  setSegmentRestartedPlaybackGlobal,
  setSegmentResumedPlaybackGlobal,
} from '@foxcorp/delta-web-analytics';
import { redirectToExternalUrl } from 'helpers/RouteHelper';
import { logPlayerEvent } from 'helpers/LogReportHelpers';
import { COLLECTION_HERO_LOGO_IMAGE, renderImage } from 'helpers/ImageHelper';
import { buildInfoTags } from './InfoTagHelper';
import InfoTagStyle from './InfoTags.module.scss';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false,
    displayDescription: true,
    displayLocationRequired: true,
    titleLines: 2,
  },
  [SITE_PLATFORM_WEB]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false,
    displayDescription: true,
    displayLocationRequired: true,
    overflowCtaEnabled: false,
    titleLines: 2,
  },
  [SITE_PLATFORM_TABLET]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false,
    displayDescription: true,
    displayLocationRequired: true,
    overflowCtaEnabled: false,
    titleLines: 2,
  },
  [SITE_PLATFORM_MOBILE]: {
    canDisplayRestartButton: true,
    enableTruncateDesc: false, // Disable 'More Info' CTA
    displayDescription: false,
    displayLocationRequired: true,
    overflowCtaEnabled: true,
    titleLines: 3,
  },
};
const SLIDE_THRESHOLD = 50;
let slideDragged = false;
let leftBound;

class InfoTags extends Component {
  componentDidMount() {
    this.heroClickthrough.addEventListener('mousedown', this.heroClickMouseDown);
    this.heroClickthrough.addEventListener('mouseup', this.heroClickMouseUp);
  }

  componentWillUnmount() {
    this.heroClickthrough.removeEventListener('mousedown', this.heroClickMouseDown);
    this.heroClickthrough.removeEventListener('mouseup', this.heroClickMouseUp);
  }

  heroClickMouseDown = () => {
    slideDragged = false;
    leftBound = this.heroClickthrough.getBoundingClientRect().left;
  };

  heroClickMouseUp = () => {
    const newLeftBound = this.heroClickthrough.getBoundingClientRect().left;
    if (Math.abs(newLeftBound - leftBound) > SLIDE_THRESHOLD) slideDragged = true;
  };

  render() {
    const callSignMap = window?.ExternalConfig?.callSignRedirects || {};
    const seriesRedirectMap = window?.ExternalConfig?.seriesRedirectMap || {};

    const {
      account,
      breakpoint,
      callCollectionImpressionEvent,
      collectionInfo,
      foxAwesomeLayout,
      headerImageState,
      idx,
      isLive,
      isDescriptionOpen,
      item,
      onPrimaryButtonClick,
      onTitleClick,
      onWatch,
      overridePlayButtonClick,
      overridePlayButtonLink,
      router,
      saveLiveRestart,
      saveLiveVideo,
      setSuggestedChannel,
      smallCollectionHeader,
      style,
      useSeriesLogo,
      toggleDescription,
    } = this.props;

    const {
      canDisplayRestartButton,
      displayDescription,
      enableTruncateDesc,
      titleLines,
    } = BREAKPOINTS[breakpoint];

    const {
      badge,
      callSign,
      description,
      headline,
      alternativeHeadline,
      endDate,
      fadeButtons,
      isSeriesDetailAvailable,
      name,
      originalVideo,
      previewLink,
      seriesLogo,
      seriesName,
      startDate,
      tabIndex,
      title,
      titleLink,
      videoId,
    } = item;

    const infotagHelperProps = {
      canDisplayRestartButton,
      foxAwesomeLayout,
      isDescriptionOpen,
      isSeriesDetailAvailable,
      item,
      style,
      toggleDescription,
      collectionInfo,
      headerImageState,
      idx,
      account,
    };
    const {
      descriptionComponent,
      highlightClass,
      highlightText,
      isPreviewSecondaryBtn,
      isRestartBtn,
      isRestartSecondaryBtn,
      isResumeBtn,
      meta,
      primaryButtonIsPlayButton,
      primaryButtonLink,
      primaryButtonText,
      secondaryButtonClasses,
      secondaryButtonLink,
      secondaryButtonText,
      showName,
      showProgressBar,
      tertiaryButtonLink,
      tertiaryButtonText,
      tertiaryButtonClasses,
      handleButtonClickAnalyticsEvent,
      BUTTON_TYPE,
    } = buildInfoTags({ ...infotagHelperProps, canDisplayRestartButton });

    const itemIsSportsContent = isSportsContent(item);
    const isMobile = breakpoint === SITE_PLATFORM_MOBILE;

    let badgeTag = null;
    if (badge) {
      badgeTag = (
        <Badge
          theme={style}
          badge={badge}
          title={title}
          startDate={getTimestamp(startDate)}
          tabIndex={tabIndex}
        />
      );
    }
    const progressBarComponent = showProgressBar ? (
      <ProgressBar endDate={endDate} startDate={startDate} theme={style} />
    ) : null;

    const primaryButtonClasses = [style.showButton];
    if (fadeButtons) primaryButtonClasses.push(style.fadeButtons);
    if (ShowcaseRedesign) primaryButtonClasses.push(style.squared);

    const handleLiveClick = () => {
      setSegmentContextSourceLive(true);
      if (EpgEnabled) {
        setSuggestedChannel({ network: callSign, videoId });
      } else {
        saveLiveVideo(originalVideo);
      }
    };

    const handleTitleClick = () => {
      callCollectionImpressionEvent();
      if (itemIsSportsContent) handleLiveClick();
      onTitleClick({});
    };

    const handlePrimaryClick = e => {
      const { headline } = item;
      handleButtonClickAnalyticsEvent(BUTTON_TYPE.PRIMARY);
      callCollectionImpressionEvent();
      if (item?.isUserOutOfMarket) {
        setOutOfMarketLocalCookies({
          livePlayerScreenUrl: item?.livePlayerScreenUrl,
          image: item?.stillBackgroundImage,
        });
      }
      if (item?.collectionType === COLLECTION_TYPE_LOCAL_CLIPS) {
        setLocalChannelCookies({
          headline,
          playerScreenUrl: item?.playerScreenUrl,
          videoId: item?.id,
        });
      }
      if (primaryButtonIsPlayButton) {
        logPlayerEvent('onVideoPlayClick', { video_id: videoId, video_name: name });
        onPrimaryButtonClick(e);
      }
      if (isLive) handleLiveClick();
      if (onWatch) onWatch();
      if (isResumeBtn) {
        setSegmentResumedPlaybackGlobal(true);
      }
      if (isRestartBtn) {
        saveLiveRestart({ video: item });
        setSegmentRestartedPlaybackGlobal(true);
      }
    };

    const handleSecondaryClick = () => {
      handleButtonClickAnalyticsEvent(BUTTON_TYPE.SECONDARY);
      const { toggleVideoModal } = this.props;
      callCollectionImpressionEvent();
      if (isLive) handleLiveClick();
      if (onWatch) onWatch();
      if (isRestartSecondaryBtn) {
        saveLiveRestart({ video: item });
        setSegmentRestartedPlaybackGlobal(true);
      }
      if (isPreviewSecondaryBtn) {
        toggleVideoModal({ url: previewLink, videoData: item, panelIdx: idx });
      }
    };

    const handleTertiaryClick = () => {
      handleButtonClickAnalyticsEvent(BUTTON_TYPE.TRETIARY);
      if (onWatch) onWatch();
    };

    const renderCTAs = () => {
      const hasActionButtons = !_.isEmpty(displayActionButtons);

      const desktopCTAs = (
        <div className={classNames(InfoTagStyle.desktopCTAContainer, InfoTagStyle.buttons)}>
          {hasActionButtons &&
            _.map(displayActionButtons, button =>
              isInternalLink(button.link) ? (
                <Link
                  key={button.text}
                  to={button.link}
                  onClick={button.onClick}
                  onMouseDown={button.onMouseDown}
                  className={classNames([button.classes])}
                  data-testid={button.dataTest}
                  aria-label={
                    Array.isArray(button.text)
                      ? `${button.text[1]} ${showName}`
                      : `${button.text} ${showName}`
                  }
                  role={button.role}
                  aria-hidden={isMobile}
                  tabIndex={tabIndex}
                >
                  {button.text}
                </Link>
              ) : (
                <a
                  key={button.text}
                  href={button.link}
                  onMouseDown={button.onMouseDown}
                  className={classNames([button.classes])}
                  data-testid={button.dataTest}
                  aria-label={
                    Array.isArray(button.text)
                      ? `${button.text[1]} ${showName}`
                      : `${button.text} ${showName}`
                  }
                  role={button.role}
                  aria-hidden={isMobile}
                  tabIndex={tabIndex}
                >
                  {button.text}
                </a>
              ),
            )}
          {isDescTooLong(description) && enableTruncateDesc ? (
            <MoreInfoButton
              key={moreInfoKey}
              className={style.moreLink}
              value={moreInfoText}
              description={description}
              ariaHidden={isMobile}
              tabIndex={tabIndex}
            />
          ) : null}
        </div>
      );

      let mobileCTAs = null;
      if (button) {
        mobileCTAs = (
          <div className={classNames(InfoTagStyle.mobileCTAContainer, InfoTagStyle.buttons)}>
            {isInternalLink(button.link) ? (
              <Link
                key={button.text}
                to={button.link}
                role="button"
                onClick={button.onClick}
                className={classNames(button.classes)}
                data-testid={button.dataTest}
                aria-hidden={!isMobile}
                aria-label={
                  Array.isArray(button.text)
                    ? `${button.text[1]} ${showName}`
                    : `${button.text} ${showName}`
                }
                tabIndex={tabIndex}
              >
                {button.text}
              </Link>
            ) : (
              <a
                key={button.text}
                href={button.link}
                role="button"
                className={classNames(button.classes)}
                data-testid={button.dataTest}
                aria-hidden={!isMobile}
                aria-label={
                  Array.isArray(button.text)
                    ? `${button.text[1]} ${showName}`
                    : `${button.text} ${showName}`
                }
                tabIndex={tabIndex}
              >
                {button.text}
              </a>
            )}
          </div>
        );
      }
      return (
        <>
          {desktopCTAs}
          {mobileCTAs}
        </>
      );
    };

    const isClipsLocalShowcase =
      item?.videoType === VIDEO_TYPE && collectionInfo?.categoryType === COLLECTION_TYPE_FTSLOCAL;
    const isOverridePlayButton = overridePlayButtonLink && primaryButtonIsPlayButton;
    let linkOverride = null;
    let secondaryLinkOverride = null;
    if (callSignMap[callSign] && isLive) {
      linkOverride = callSignMap[callSign];
    }

    if (item?.seriesType === 'series' && seriesRedirectMap?.[item?.showCode]) {
      linkOverride = seriesRedirectMap[item.showCode];
      secondaryLinkOverride = seriesRedirectMap[item.showCode];
    }

    if (item?.originalVideo?.deeplinkWeb) {
      linkOverride = item.originalVideo.deeplinkWeb;
    }
    const actionButtons = [];
    actionButtons.push({
      link: linkOverride || (!isOverridePlayButton && primaryButtonLink),
      onClick: isOverridePlayButton
        ? () => overridePlayButtonClick({ visibility: true, redirectUrl: primaryButtonLink })
        : handlePrimaryClick,
      onMouseDown: e =>
        e.button === 1 ? handleButtonClickAnalyticsEvent(BUTTON_TYPE.PRIMARY) : noop,
      classes: primaryButtonClasses,
      text: primaryButtonText,
      dataTest: 'home-slider-primary-button',
      role: 'button',
    });
    actionButtons.push({
      link: secondaryLinkOverride || secondaryButtonLink,
      onClick: handleSecondaryClick,
      onMouseDown: e =>
        e.button === 1 ? handleButtonClickAnalyticsEvent(BUTTON_TYPE.SECONDARY) : noop,
      classes: secondaryButtonClasses,
      text: secondaryButtonText,
      dataTest: 'home-slider-secondary-button',
      role: 'button',
    });
    actionButtons.push({
      link: secondaryLinkOverride || tertiaryButtonLink,
      onClick: handleTertiaryClick,
      onMouseDown: e =>
        e.button === 1 ? handleButtonClickAnalyticsEvent(BUTTON_TYPE.TRETIARY) : noop,
      classes: tertiaryButtonClasses,
      text: tertiaryButtonText,
      role: 'button',
    });
    const displayActionButtons = _.filter(actionButtons, a => !!a.text);

    const foxAwesomeContainerStyles = [style.infoTagsRedesign];
    const truncateLength = 50;
    const kicker = (
      <div className={classNames(style.kicker)}>
        {truncateText(alternativeHeadline, truncateLength)}
      </div>
    );
    const headLineText = (
      <Dotdotdot clamp={2} useNativeClamp>
        {headline}
      </Dotdotdot>
    );
    const isSeriesLogoImage = useSeriesLogo && seriesLogo;
    foxAwesomeContainerStyles.push(style.smallerInfoContainer);
    if (smallCollectionHeader) foxAwesomeContainerStyles.push(style.withSmallHeader);

    const isPromo = _.get(item, 'originalVideo.@type') === 'Promo';

    const seriesLogoOrText = isSeriesLogoImage ? (
      renderImage({
        alt: seriesName,
        breakpoint,
        className: style.seriesLogo,
        dataTest: 'info-tags-series-logo',
        imageType: COLLECTION_HERO_LOGO_IMAGE,
        src: seriesLogo,
        'aria-label': showName,
        role: 'link',
        lazyLoad: false,
      })
    ) : (
      <Dotdotdot
        clamp={isClipsLocalShowcase ? 3 : titleLines}
        useNativeClamp
        aria-label={showName}
        role="link"
      >
        {showName}
      </Dotdotdot>
    );
    const moreInfoText = _.get(window.Language, 'global_moreInfoButton');
    const moreInfoKey = moreInfoText.toLowerCase(_.replace(' ', '-'));
    const button = displayActionButtons[0];
    const networksBar = window.ExternalConfig.networkNavigation;
    const promoTypeIsShowcaseBurntIn = originalVideo?.promoType === 'showcaseBurntIn';
    const heroPlayerCTAClasses = classNames(
      style.heroPlayerCTA,
      networksBar ? style.withNetworksBar : null,
    );

    const showKicker = (ShowcaseRedesign && alternativeHeadline && isPromo) || alternativeHeadline;

    const showcaseTitleImage = isInternalLink(secondaryLinkOverride || titleLink) ? (
      <Link
        data-test="info-tags-title-link"
        key="homeTitleLink"
        onClick={handleTitleClick}
        to={titleLink}
        className={style.showName}
        tabIndex={tabIndex}
      >
        {seriesLogoOrText}
      </Link>
    ) : (
      <a
        data-test="info-tags-title-link"
        key="homeTitleLink"
        onClick={handleTitleClick}
        href={secondaryLinkOverride || titleLink}
        className={style.showName}
        tabIndex={tabIndex}
      >
        {seriesLogoOrText}
      </a>
    );
    return (
      <>
        <div
          ref={elem => {
            this.heroClickthrough = elem;
          }}
          draggable={false}
          className={heroPlayerCTAClasses}
          onClick={e => {
            if (isInternalLink(button?.link)) {
              browserHistory.push(button?.link);
            } else {
              redirectToExternalUrl(button?.link);
            }
            if (slideDragged || !promoTypeIsShowcaseBurntIn) return;
            const link = promoTypeIsShowcaseBurntIn ? originalVideo.url : button?.link;
            e.target.href = link;
            if (!promoTypeIsShowcaseBurntIn) {
              router.push(link);
              button.onClick(e);
            } else {
              window.location = link;
            }

            e.stopPropagation();
          }}
        />
        {!promoTypeIsShowcaseBurntIn ? (
          <a
            href={button?.link}
            className={classNames([style.infoContainer, ...foxAwesomeContainerStyles])}
          >
            {!showKicker ? badgeTag : null}
            <div className={style.infoTagsRedesignContainer}>
              {ShowcaseRedesign && headline && isPromo ? (
                <div className={style.headline}>{headLineText}</div>
              ) : (
                showcaseTitleImage
              )}
              {showKicker ? kicker : null}
              <div className={style.description}>
                <span className={highlightClass} data-test="info-tags-highlight">
                  {highlightText}
                </span>
                {displayDescription && descriptionComponent}
                {progressBarComponent}
                <div className={style.detailContainer}>
                  <span className={style.meta}>{meta}</span>
                </div>
              </div>
              {renderCTAs()}
            </div>
          </a>
        ) : null}
      </>
    );
  }
}

InfoTags.defaultProps = {
  account: null,
  breakpoint: null,
  callCollectionImpressionEvent: () => {},
  collectionInfo: {},
  idx: null,
  foxAwesomeLayout: false,
  headerImageState: null,
  isLive: false,
  isDescriptionOpen: false,
  location: {},
  onPrimaryButtonClick: () => {},
  onWatch: () => {},
  overridePlayButtonClick: () => {},
  overridePlayButtonLink: false,
  smallCollectionHeader: false,
  style: {},
  toggleVideoModal: () => {},
  toggleDescription: () => {},
  useSeriesLogo: false,
};

InfoTags.propTypes = {
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  callCollectionImpressionEvent: PropTypes.func,
  collectionInfo: PropTypes.shape({}),
  foxAwesomeLayout: PropTypes.bool,
  headerImageState: PropTypes.string,
  idx: PropTypes.number,
  isLive: PropTypes.bool,
  isDescriptionOpen: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({}),
  onPrimaryButtonClick: PropTypes.func,
  onTitleClick: PropTypes.func.isRequired,
  onWatch: PropTypes.func,
  overridePlayButtonClick: PropTypes.func,
  overridePlayButtonLink: PropTypes.bool,
  router: PropTypes.shape({}).isRequired,
  saveLiveRestart: PropTypes.func.isRequired,
  saveLiveVideo: PropTypes.func.isRequired,
  setSuggestedChannel: PropTypes.func.isRequired,
  smallCollectionHeader: PropTypes.bool,
  style: PropTypes.shape({}),
  toggleVideoModal: PropTypes.func,
  toggleDescription: PropTypes.func,
  useSeriesLogo: PropTypes.bool,
};

export default withRouter(InfoTags);
